import { gql } from '@apollo/client'
import { BodyText, LikeActiveIcon, LikeIcon, Link, Spinner, useToast } from '@farol-ds/react'
import { VoteTypeEnum } from '@jusbrasil-web/shared-apollo-client/types'
import { useMemo } from 'react'

import { useVoteComment } from '../../hooks/use-vote-comment'
import {
  UpVoteButton_CommentFragment,
  UpVoteButton_ViewerFragment,
} from './__generated__/up-vote-button.graphql'
import styles from './up-vote-button.module.scss'

export interface UpVoteButtonProps {
  comment: UpVoteButton_CommentFragment
  viewer: UpVoteButton_ViewerFragment | null
  onVoteClick?: (isUpVote: boolean) => void
}

const VOTE_TYPE = {
  NONE: VoteTypeEnum.None,
  POSITIVE: VoteTypeEnum.Positive,
} as const

export function UpVoteButton(props: UpVoteButtonProps) {
  const toast = useToast()
  const { comment, viewer, onVoteClick } = props
  const { viewerUpVoted, upVotes } = comment
  const vote = viewerUpVoted ? VOTE_TYPE.NONE : VOTE_TYPE.POSITIVE
  const UpVoteButtonIcon = viewerUpVoted ? LikeActiveIcon : LikeIcon
  const voteComment = useVoteComment({ comment })

  async function handleClick() {
    if (!viewer) return
    await voteComment
      .vote({
        variables: {
          input: {
            commentId: comment.id,
            vote,
          },
        },
      })
      .catch((error) => {
        console.error(error)
        toast('Erro ao curtir comentário', { type: 'negative' })
      })
    onVoteClick?.(vote === VOTE_TYPE.POSITIVE)
  }

  const upVoteLabel = useMemo(() => {
    if (upVotes < 1) return 'Curtir'
    if (upVotes === 1) return '1 curtida'
    return `${upVotes} curtidas`
  }, [upVotes])

  return (
    <div className={styles.container}>
      {voteComment.loading ? (
        <Spinner />
      ) : (
        <Link type="tertiary" className={styles.voteAction} data-testid="upVoteButton" asChild>
          <button onClick={handleClick}>
            <UpVoteButtonIcon size="sm" />
            <BodyText size="md">{upVoteLabel}</BodyText>
          </button>
        </Link>
      )}
    </div>
  )
}

UpVoteButton.fragments = {
  comment: gql`
    fragment UpVoteButton_comment on DocumentComment {
      viewerUpVoted
      upVotes
      ...UseVoteComment_comment
    }
    ${useVoteComment.fragments.comment}
  `,
  viewer: gql`
    fragment UpVoteButton_viewer on Profile {
      pid
    }
  `,
}
